import type { Card } from '../../engine/card.js';
import type { GameState, Pile } from '../../engine/game-state.js';
import type { Move } from '../../engine/moves.js';
import type { SolitaireRules } from '../../engine/rules.js';

// 3 peaks × (row1: 1, row2: 2, row3: 3) = 18 face-down peak cards.
// Plus a 10-card face-up base row shared across the bottom of all peaks.
export const PEAK_IDS = [
  'p11', 'p12', 'p13',
  'p21', 'p22', 'p23', 'p24', 'p25', 'p26',
  'p31', 'p32', 'p33', 'p34', 'p35', 'p36', 'p37', 'p38', 'p39',
  'p41', 'p42', 'p43', 'p44', 'p45', 'p46', 'p47', 'p48', 'p49', 'p4-10',
] as const;
export type PeakId = (typeof PEAK_IDS)[number];

// `COVERS[id]` = the two cards directly below this card in the visual stack.
// While EITHER cover still has a card, this card stays face-down.
export const COVERS: Record<string, readonly string[]> = {
  // Row 1 covered by row 2 (each peak independent)
  p11: ['p21', 'p22'],
  p12: ['p23', 'p24'],
  p13: ['p25', 'p26'],
  // Row 2 covered by row 3
  p21: ['p31', 'p32'],
  p22: ['p32', 'p33'],
  p23: ['p34', 'p35'],
  p24: ['p35', 'p36'],
  p25: ['p37', 'p38'],
  p26: ['p38', 'p39'],
  // Row 3 covered by row 4 (peaks share corners at the base)
  p31: ['p41', 'p42'],
  p32: ['p42', 'p43'],
  p33: ['p43', 'p44'],
  p34: ['p44', 'p45'],
  p35: ['p45', 'p46'],
  p36: ['p46', 'p47'],
  p37: ['p47', 'p48'],
  p38: ['p48', 'p49'],
  p39: ['p49', 'p4-10'],
  // Row 4: bottom - no covers.
};

function peakIsUncovered(state: GameState, pileId: string): boolean {
  const covers = COVERS[pileId] ?? [];
  for (const c of covers) {
    if ((state.piles[c]?.cards.length ?? 0) > 0) return false;
  }
  return true;
}

function rankWrapDistance(a: number, b: number): number {
  const diff = Math.abs(a - b);
  return Math.min(diff, 13 - diff);
}

function emptyPile(id: string, type: Pile['type']): Pile {
  return { id, type, cards: [] };
}

function setupTriPeaks(deck: Card[]): GameState {
  const piles: Record<string, Pile> = {};
  for (const id of PEAK_IDS) piles[id] = emptyPile(id, 'peak');
  piles.stock = emptyPile('stock', 'stock');
  piles.waste = emptyPile('waste', 'waste');

  const d = deck.slice();
  // Rows 1–3 face-down (18 cards), row 4 face-up (10 cards).
  for (let i = 0; i < 18; i++) {
    const c = d.shift()!;
    c.faceUp = false;
    piles[PEAK_IDS[i]!]!.cards.push(c);
  }
  for (let i = 18; i < 28; i++) {
    const c = d.shift()!;
    c.faceUp = true;
    piles[PEAK_IDS[i]!]!.cards.push(c);
  }
  // Seed the waste with one card so the player can start matching immediately.
  const seed = d.shift()!;
  seed.faceUp = true;
  piles.waste!.cards.push(seed);
  // Remaining 23 to stock face-down.
  for (const c of d) {
    c.faceUp = false;
    piles.stock!.cards.push(c);
  }
  return { piles, movesCount: 0 };
}

export const tripeaksRules: SolitaireRules = {
  id: 'tripeaks',

  setup(deck) {
    return setupTriPeaks(deck);
  },

  canMove(state, move: Move) {
    const from = state.piles[move.from];
    const to = state.piles[move.to];
    if (!from || !to) return false;
    if (from === to) return false;

    // Stock → waste: top of stock only.
    if (from.type === 'stock') {
      if (to.id !== 'waste') return false;
      const top = from.cards[from.cards.length - 1];
      return !!top && top.id === move.cardId;
    }

    // Peak → waste: card must be face-up (uncovered) and rank ±1 from waste top (with K↔A wrap).
    if (from.type !== 'peak') return false;
    if (to.id !== 'waste') return false;

    const card = from.cards[from.cards.length - 1];
    if (!card || card.id !== move.cardId) return false;
    if (!card.faceUp) return false;

    const wasteTop = to.cards[to.cards.length - 1];
    if (!wasteTop) return false;
    return rankWrapDistance(card.rank, wasteTop.rank) === 1;
  },

  isWon(state) {
    for (const id of PEAK_IDS) {
      if ((state.piles[id]?.cards.length ?? 0) > 0) return false;
    }
    return true;
  },

  isLost(state) {
    if (state.piles.stock!.cards.length > 0) return false;
    if (hasAnyMatch(state)) return false;
    // Stock empty, no matches, but peaks remain → unwinnable.
    for (const id of PEAK_IDS) {
      if ((state.piles[id]?.cards.length ?? 0) > 0) return true;
    }
    return false;
  },

  afterMove(state) {
    // Flip any peak card that is now uncovered.
    for (const id of PEAK_IDS) {
      const pile = state.piles[id];
      if (!pile || pile.cards.length === 0) continue;
      const card = pile.cards[0]!;
      if (card.faceUp) continue;
      if (peakIsUncovered(state, id)) card.faceUp = true;
    }
  },
};

export function drawFromStock(state: GameState): boolean {
  const stock = state.piles.stock!;
  const waste = state.piles.waste!;
  if (stock.cards.length === 0) return false;
  const c = stock.cards.pop()!;
  c.faceUp = true;
  waste.cards.push(c);
  return true;
}

export function hasAnyMatch(state: GameState): boolean {
  const wasteTop = state.piles.waste!.cards[state.piles.waste!.cards.length - 1];
  if (!wasteTop) return false;
  for (const id of PEAK_IDS) {
    const pile = state.piles[id];
    if (!pile || pile.cards.length === 0) continue;
    const card = pile.cards[0]!;
    if (!card.faceUp) continue;
    if (rankWrapDistance(card.rank, wasteTop.rank) === 1) return true;
  }
  return false;
}
